import clsx from '@vangst/lib/clsx'
import { QuizType } from '@vangst/services/oogst/training/useQuiz'
import { TrainingFragment } from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import Image from 'next/image'
import { MdCheckCircle, MdError } from 'react-icons/md'
import ClickyBtn from '../../components/navigation/ClickyBtn'
import Progress from '../../components/views/Progress'

type PropsType = {
  readonly quiz: QuizType
  readonly buttonText: string
  readonly training: TrainingFragment
  readonly handleStartQuiz: () => void
}

export default function CourseResults(props: PropsType) {
  const { handleStartQuiz, quiz, training, buttonText } = props

  const total = quiz?.totalQuestions
  const numToPass = quiz?.totalCorrectToGainCertification
  const sectionsLeftToTake = !training?.currentUserPassed

  const currentIndex = training.quizzes.findIndex((q) => q.id === quiz?.id)
  const nextQuiz = training.quizzes[currentIndex + 1]
  const nextQuizUrl = Routes?.COMPANIES_COURSE?.replace(
    ':displayname',
    training.creatorClient.displayname,
  )
    ?.replace(':tid', training.slug ?? '')
    ?.replace(':id', nextQuiz?.slug)

  const badgeUrl = training?.credential?.badgeUrl || Routes.GENERIC_CREDENTIAL

  return (
    <>
      <span
        className={clsx(
          'text-sm',
          quiz?.userPassed ? 'text-blue-dark' : 'text-red',
        )}
      >
        <strong>{quiz?.numberOfQuestionsCorrect} </strong>of{' '}
        <strong>{quiz?.totalQuestions} </strong>
        correct.
      </span>
      <Progress
        className="h-3 bg-red text-blue-dark"
        id="quiz-complete-progress"
        value={quiz?.progressWidthAfterComplete}
      />
      <div
        className={clsx(
          'my-4 flex items-center gap-x-4 font-600',
          quiz?.userPassed ? 'text-blue-dark' : 'text-red',
        )}
      >
        {quiz?.userPassed ? (
          <MdCheckCircle className="size-9" />
        ) : (
          <MdError className="size-9" />
        )}
        <span className="text-lg">
          {quiz?.userPassed
            ? sectionsLeftToTake
              ? 'Congratulations, you passed!'
              : "Congratulations, you've passed all the courses in this training!"
            : 'Unfortunately, you did not pass.'}
        </span>
      </div>
      {!sectionsLeftToTake && (
        <div>
          <Image
            alt={quiz?.title ?? 'Credential Obtained'}
            className="m-auto my-4 object-cover sm:size-72"
            height={286}
            width={286}
            src={badgeUrl}
          />
        </div>
      )}

      <span
        className={clsx(
          'border-b py-4 text-sm sm:py-8',
          quiz?.userPassed ? 'text-blue-dark' : 'text-red',
        )}
      >
        {quiz?.userPassed
          ? sectionsLeftToTake
            ? "You've received a passing grade on this quiz, please continue with the next part!"
            : 'These results will be visible to recruiters when considering applications to job postings.'
          : `In order to continue, you must achieve a passing score of ${numToPass} of ${total} answers correctly. Please try again by retaking the test below.`}
      </span>
      {!quiz?.userPassed ? (
        <ClickyBtn
          as="text-icon"
          className={clsx(
            'mt-4',
            quiz?.userPassed ? 'mood-blue-dark' : 'mood-red',
          )}
          onClick={handleStartQuiz}
        >
          Retake Quiz
        </ClickyBtn>
      ) : (
        <>
          {sectionsLeftToTake ? (
            <ClickyBtn href={nextQuizUrl} className="mood-blue-dark mt-4">
              {buttonText}
            </ClickyBtn>
          ) : (
            <ClickyBtn
              href={`${Routes.COMPANIES}/${training.creatorClient.displayname}`}
              className="mood-blue-dark mt-4"
            >
              Return to company profile
            </ClickyBtn>
          )}
        </>
      )}
    </>
  )
}
